import React from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useDisplayField } from '../../hooks/useDisplayField';
import { formatDate } from '../utils/formatDate';

interface MetaDrivenTableProps {
  headers: any[];
  data: any[];
  dataMetrics: any;
  actions?: any;
  sortDisabled?: boolean;
  order?: string;
  orderBy?: 'asc' | 'desc';
  createSortHandler?: (orderBy: string) => void;
  small?: boolean;
}

const renderCell = (
  alias_name: string,
  row: any,
  metadata: any,
  dataMetrics: any,
) => {
  const { getMasterDataLabel } = useDisplayField(metadata);

  const value = row[alias_name];
  const meta = metadata.find(
    (meta: { alias_name: string }) => meta.alias_name === alias_name,
  );

  const metric = dataMetrics[alias_name];

  if (!meta) return value ?? 'N/A';

  if (metric?.type === 'component' && metric.element) {
    return metric.element(row);
  }

  if (metric?.type === 'date') {
    return formatDate(value);
  }

  if (metric?.type === 'date_and_time') {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (meta.domain) {
    const domainMap = getMasterDataLabel(value, alias_name);
    return domainMap;
  }

  return value ?? 'N/A';

};

export const MetaDrivenTable: React.FC<MetaDrivenTableProps> = ({
  headers,
  dataMetrics,
  data,
  actions = [],
  sortDisabled,
  order,
  orderBy,
  createSortHandler,
  small = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const orderedHeaders = headers
    .filter((item: { is_show: boolean }) => item.is_show)
    .sort((a: { display_order: number }, b: { display_order: number }) => {
      return a.display_order - b.display_order;
    })
    .map((item) => item);

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0 }}>
      <Table stickyHeader size={small || isMobile || isTablet ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {orderedHeaders.map((value: any, index: number) => (
              <TableCell
                key={index}
                sx={{
                  ...value?.style,
                  textAlign: value?.style?.textAlign || 'left',
                }}
              >
                <TableSortLabel
                  disabled={sortDisabled}
                  active={order === value.alias_name}
                  direction={orderBy || 'asc'}
                  onClick={() => {
                    if (createSortHandler)
                      createSortHandler(value.alias_name);
                  }}
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center',
                    color: value?.style?.color,
                    textAlign: value?.style?.textAlign || 'left',
                    justifyContent: value?.style?.textAlign === 'center'
                      ? 'center'
                      : value?.style?.textAlign === 'right'
                        ? 'flex-end'
                        : 'flex-start',
                    '&:hover': {
                      color: value?.style?.color,
                    },
                    '&.Mui-active': {
                      color: value?.style?.color,
                    },
                  }}
                >
                  {value?.label}
                </TableSortLabel>
              </TableCell>
            ))}
            {data.length != 0 && actions.length !== 0 && (
              <TableCell>Actions</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody sx={{ flex: 1, minHeight: 0 }}>
          {data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={orderedHeaders.length}
                sx={{ textAlign: 'center', py: small ? 2 : 4 }}
              >
                No data available
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {orderedHeaders.map((col) => {
                  const hasComponentRenderer =
                    dataMetrics?.[col?.alias_name]?.type === 'component';
                  return (
                    <TableCell
                      key={col.alias_name}
                      sx={{
                        ...dataMetrics?.[col?.alias_name]?.style,
                        ...(!hasComponentRenderer && {
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          maxWidth: '200px',
                          textOverflow: 'ellipsis',
                        }),
                        ...(hasComponentRenderer && {
                          overflow: 'visible',
                          whiteSpace: 'normal',
                        }),
                      }}
                    >
                      {renderCell(
                        col.alias_name,
                        row,
                        orderedHeaders,
                        dataMetrics,
                      ) || 'N/A'}
                    </TableCell>
                  );
                })}
                {actions.length !== 0 && (
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        gap: 1,
                      }}
                    >
                      {actions.map((value: { isValidate: any; isHidden: (arg0: any) => any; type: any; isDisable: (arg0: any) => boolean | undefined; handleClick: (arg0: any) => void; label: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | ((arg0: any) => string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined) | null | undefined; }, key: React.Key | null | undefined) =>
                        (value.isValidate && value.isHidden ? value.isHidden(row) : true) && (
                          <IconButton
                            disabled={value.isDisable ? value.isDisable(row) : false}
                            onClick={() => value.handleClick(row)}
                            sx={{ p: 0.3, m: 0 }}
                          >
                            {typeof value.label === 'function'
                              ? value.label(row)
                              : value.label}
                          </IconButton>
                        )
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};